import React, { useState, useEffect } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Loader from "../../../../components/Common/Loader";
import {
  DELETEPRICE,
  ROOMLIST,
  ROOMPRICING,
  ROOMPRICINGDETAIL,
  ROOMTYPE,
} from "../../../../services/ApiCalls";
import Slider from "react-slick";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const roomSchema = z.object({
  roomId: z.string().min(1, { message: "Please select a room type" }),
  ratePricing: z.array(
    z.object({
      durationType: z.string().min(1, { message: "Duration type required" }),
      basePrice: z
        .number()
        .min(0, { message: "Base price cannot be negative" }),
      discount: z
        .number()
        .min(0, { message: "Discount cannot be negative" })
        .max(100, { message: "Discount cannot exceed 100%" }),
      refundable: z.boolean(),
    })
  ),
});

const PricingDetail = () => {
  const [pricingDetail, setPricingDetail] = useState();
  const [loader, setLoader] = useState(false);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const [hotelAdminData, setHotelAdminData] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      roomId: "",
      ratePricing: [
        { durationType: "", basePrice: 0, discount: 0, refundable: true },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ratePricing",
  });

  const getAmenityData = async () => {
    try {
      const roomRes = await ROOMTYPE({ limit: 100, page: 1 });
      if (roomRes?.data?.status === "success") {
        setHotelAdminData(roomRes?.data?.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getData = async () => {
    try {
      setLoader(true);
      const res = await ROOMPRICINGDETAIL({ id });
      if (res?.data?.status === "success") {
        setPricingDetail(res.data?.data);
      }
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getAmenityData();
    if (id) getData();
  }, [id]);

  const submitHandler = async (data) => {
    setLoader(true);
    const payload = {
      ...data,
      roomId: id,
    };
    try {
      const res = await ROOMPRICING(payload);
      if (res?.data?.status === "success") {
        toast.success(res?.data?.message);
        getData();
      } else {
        toast.error(res?.data?.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  };

  const handleDeleteRate = async (data) => {
    const payload = {
      id: id,
      priceId: data,
    };

    let res = await DELETEPRICE(payload);
    if (res?.data?.status === "success") {
      toast.success(res?.data?.message);
      getData();
    } else {
      toast.error(res?.data?.message);
    }
  };

  const handleCancel = () => navigate("/pricing");

  console.log(pricingDetail, "pricingDetail");

  return (
    <>
      {loader && <Loader />}
      <section className="pricingDetail py-3 position-relative">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-2">
                <Link
                  to={`/pricing?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {t("pricing.detailTitle")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                {pricingDetail ? (
                  <Row className="justify-content-between">
                    <Col md={6} className="my-2">
                      <ul className="list-unstyled ps-0 mb-0 notLastBorder pe-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.hotelName")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.hotelId?.hotelName}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageHotel.columns.hotelId")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.hotelId?.hotelId}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.base_price")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.basePrice ?? 0}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.final_price")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.finalPrice ?? 0}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.discount")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.discount ?? 0}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.createdAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(pricingDetail?.createdAt).format("YYYY-MM-DD")}
                          </h6>
                        </li>

                      </ul>
                    </Col>

                    <Col md={6} className="my-2">
                      <ul className="list-unstyled mb-0 notLastBorder ps-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.address")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.hotelId?.address}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.updatedAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(pricingDetail?.updatedAt).format("YYYY-MM-DD")}
                          </h6>
                        </li>

                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t(
                              "pricing.columns.roomType",
                              "Number of People Allowed."
                            )}
                            :
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.roomType?.roomType.name}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.pricingLabel", "Area Of Room")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.pricingLabel}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.durationHours", "Area Of Room")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {pricingDetail?.durationHours}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.available")}:
                          </p>
                          <Form.Check
                            type="switch"
                            id="status-switch"
                            checked={pricingDetail?.isAvailable}

                            style={{ transform: "scale(1.4)", cursor: "pointer", marginLeft: 20 }}
                            className="form-switch-success"
                          />
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("pricing.columns.refundable")}:
                          </p>
                          <Form.Check
                            type="switch"
                            id="status-switch"
                            checked={pricingDetail?.refundable}

                            style={{ transform: "scale(1.4)", cursor: "pointer", marginLeft: 20 }}
                            className="form-switch-success"
                          />
                        </li>




                      </ul>
                    </Col>


                  </Row>
                ) : (
                  <p className="text-center text-muted">
                    No pricing details found
                  </p>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default PricingDetail;
